import type { ReactElement } from 'react'
import React, {
  useLayoutEffect,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
  Suspense,
  useState,
  useEffect,
  useId,
  memo,
} from 'react'
import { fastDeepEqual } from '~shared/utils'
import { ModalManager } from '../lib/modal-manager' // Path to your updated manager
import type {
  AnyManagedComponent,
  ControlProps,
  ModalControls,
  ModalInstance,
  ModalOptions,
  ModalStateControls,
  PropsOf,
} from '../model/types'

interface ModalContextValue {
  store: ModalManager
}

const ModalContext = createContext<ModalContextValue | null>(null)
const CurrentModalKeyContext = createContext<string | null>(null)

/**
 * Modal system provider.
 *
 * App should be wrapped with this provider once to enable the modal system.
 *
 * @example
 *   ;```tsx
 *   import { ModalProvider } from '@/features/modal-manager'
 *
 *   export function App() {
 *     return (
 *       <ModalProvider>
 *         <Routes />
 *       </ModalProvider>
 *     )
 *   }
 *   ```
 *
 * @public
 */
export function ModalProvider({ children }: { children: React.ReactNode }) {
  const storeRef = useRef<ModalManager>(undefined)

  if (!storeRef.current) {
    storeRef.current = new ModalManager()
  }

  const contextValue = useMemo<ModalContextValue>(
    () => ({ store: storeRef.current! }),
    [],
  )

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <ModalHost />
    </ModalContext.Provider>
  )
}

/**
 * Create and control a modal instance.
 *
 * Returns a tuple `[controls, render]` where: - `controls` exposes imperative
 * methods: `show`, `hide`, `destroy`, `isOpen`. - `render` is a function you
 * call in render to provide default props for the modal.
 *
 * You can also override/merge props at `show()` time.
 *
 * @example
 *   Imperative show with optional props
 *   ```tsx
 *   const [upgradeModal, renderUpgradeModal] = useModal(UpgradeModal)
 *
 *   // Later, open the modal with custom message
 *   upgradeModal.show({ message: 'Custom message during show' })
 *
 *   // In JSX, provide default props (merged with props from show)
 *   {renderUpgradeModal({ message: 'Message during render' })}
 *   ```
 *
 * @param component The modal component to manage (can be lazy-loaded).
 * @param options Optional configuration like `destroyOnClose`.
 *
 * @returns `[controls, render]` tuple.
 *
 * @public
 */
export function useModal<C extends AnyManagedComponent>(
  component: C,
  options?: ModalOptions,
): [ModalControls<C>, (props: PropsOf<C>) => ReactElement | null] {
  const context = useContext(ModalContext)

  if (!context) {
    throw new Error('useModal must be used within ModalProvider')
  }

  const { store } = context
  const key = useId()

  // Ensure instance exists
  const instanceRef = useRef<ModalInstance>(undefined)
  if (!instanceRef.current) {
    instanceRef.current = store.ensure(key, component, options)
  }

  // Refs to track props
  const latestPropsRef = useRef<PropsOf<C>>(undefined)
  const lastAppliedPropsRef = useRef<PropsOf<C>>(undefined)

  // Control methods
  const show = useCallback(
    (props?: Partial<PropsOf<C>>): void => {
      if (props && Object.keys(props).length > 0) {
        store.mergeProps(key, props)
      }
      store.show(key)
    },
    [store, key],
  )

  const hide = useCallback((): void => {
    store.hide(key)
    if (options?.destroyOnClose) {
      setTimeout(() => store.destroy(key), 350)
    }
  }, [store, key, options?.destroyOnClose])

  const destroy = useCallback((): void => {
    store.destroy(key)
  }, [store, key])

  const isOpen = useCallback((): boolean => {
    return store.isOpen(key)
  }, [store, key])

  useLayoutEffect(() => {
    const nextProps = latestPropsRef.current

    // Only update store if props effectively changed from what we last sent
    if (nextProps && !fastDeepEqual(lastAppliedPropsRef.current, nextProps)) {
      lastAppliedPropsRef.current = nextProps
      store.updateProps(key, nextProps)
    }
  })

  const render = useCallback((props: PropsOf<C>): ReactElement | null => {
    latestPropsRef.current = props
    return null
  }, [])

  const controls = useMemo<ModalControls<C>>(
    () => ({ show, hide, destroy, isOpen }),
    [show, hide, destroy, isOpen],
  )

  useEffect(() => {
    return () => {
      store.destroy(key)
    }
  }, [store, key])

  return [controls, render]
}

/**
 * Hook for internal modal self-control.
 *
 * Use this inside a managed modal component to control its own state
 * without receiving `open`/`setOpen` from the parent.
 *
 * @example
 *   ;```tsx
 *   export default function UpgradeModal({ message = 'Upgrade' }) {
 *     const modalState = useModalState()
 *
 *     const handleUpgrade = () => {
 *       window.open('https://rundownstudio.app#pricing', '_blank')
 *       modalState.close()
 *     }
 *
 *     return (
 *       <ModalWrapper
 *         title="Time to upgrade"
 *         open={modalState.open}
 *         setOpen={modalState.setOpen}
 *         buttons={[{ text: 'Upgrade my account', onClick: handleUpgrade }]}
 *       >
 *         <p>{message}</p>
 *       </ModalWrapper>
 *     )
 *   }
 *   ```
 *
 * @public
 */
export function useModalState(): ModalStateControls {
  const context = useContext(ModalContext)
  const currentKey = useContext(CurrentModalKeyContext)

  if (!context)
    throw new Error('useModalState must be used within ModalProvider')
  if (!currentKey)
    throw new Error('useModalState must be used within a modal component')

  const { store } = context
  const [localOpen, setLocalOpen] = useState(() => store.isOpen(currentKey))

  useEffect(() => {
    return store.subscribe(() => {
      setLocalOpen(store.isOpen(currentKey))
    })
  }, [store, currentKey])

  const setOpen = useCallback(
    (nextOpen: boolean): void => {
      if (nextOpen) store.show(currentKey)
      else store.hide(currentKey)
    },
    [store, currentKey],
  )

  const show = useCallback(() => setOpen(true), [setOpen])
  const close = useCallback(() => setOpen(false), [setOpen])
  const destroy = useCallback(
    () => store.destroy(currentKey),
    [store, currentKey],
  )

  return useMemo(
    () => ({
      open: localOpen,
      setOpen,
      isOpen: localOpen,
      show,
      close,
      destroy,
    }),
    [localOpen, setOpen, show, close, destroy],
  )
}

/**
 * Renders all active modal instances.
 *
 * @private
 */
function ModalHost() {
  const context = useContext(ModalContext)

  const emptyInstances = useMemo<ModalInstance[]>(() => [], [])
  const noopSubscribe = useMemo(() => () => () => {}, [])
  const getEmptySnapshot = useCallback(() => emptyInstances, [emptyInstances])

  const subscribe = context?.store.subscribe ?? noopSubscribe
  const getSnapshot = context?.store.getSnapshot ?? getEmptySnapshot

  const instances = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  if (!context) return null

  return (
    <>
      {instances.map((instance) => (
        <ManagedInstance
          key={instance.id}
          instance={instance}
          store={context.store}
        />
      ))}
    </>
  )
}

/**
 * Wraps a single modal instance and renders it.
 * Memoized to prevent unnecessary re-renders.
 *
 * @private
 */
const ManagedInstance = memo(
  function ManagedInstance({
    instance,
    store,
  }: {
    instance: ModalInstance
    store: ModalManager
  }) {
    const { Comp, props, key, open, destroyOnClose } = instance

    // If the modal has never been opened, do not render the child component at all.
    // This prevents Lazy components from triggering network requests on initial page load.
    const hasBeenOpened = useRef(false)
    if (open) {
      hasBeenOpened.current = true
    }

    const setOpen = useCallback<ControlProps['setOpen']>(
      (nextOpen) => {
        if (nextOpen) {
          store.show(key)
        } else {
          store.hide(key)
          if (destroyOnClose) {
            setTimeout(() => store.destroy(key), 350)
          }
        }
      },
      [store, key, destroyOnClose],
    )

    if (!open && !hasBeenOpened.current) {
      return null
    }

    return (
      <CurrentModalKeyContext.Provider value={key}>
        <Suspense fallback={null}>
          <Comp
            {...props}
            open={open}
            setOpen={setOpen}
          />
        </Suspense>
      </CurrentModalKeyContext.Provider>
    )
  },
  (prev, next) => {
    // Because ModalManager is immutable, we can rely on strict reference equality.
    // - If props change, 'instance' will be a new object.
    // - If open/close changes, 'instance' will be a new object.
    // - If nothing changed for this modal, 'instance' is the same object.
    return prev.instance === next.instance
  },
)
